import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Download, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface TransferDetails {
  id: string;
  status: string;
  initiated_at: string;
  animal: {
    species: string;
    common_name: string;
    identifier: string;
  };
  from_user?: {
    name: string;
    email: string;
  };
  initiator_notes?: string;
  scope_json?: any;
}

const AcceptTransfer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const code = searchParams.get('code');
  
  const [loading, setLoading] = useState(true);
  const [transfer, setTransfer] = useState<TransferDetails | null>(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!code) {
      toast.error('Invalid transfer link');
      navigate('/');
      return;
    }

    loadTransferDetails();
  }, [code]);

  const loadTransferDetails = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-transfer', {
        body: { code },
      });

      if (error || !data?.valid) {
        toast.error('Invalid or expired transfer link');
        navigate('/');
        return;
      }

      setTransfer(data.transfer);
    } catch (error) {
      console.error('Error loading transfer:', error);
      toast.error('Failed to load transfer details');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      // Redirect to signup with transfer code
      navigate(`/auth?signup=true&transfer_code=${code}`);
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('accept-transfer', {
        body: {
          verification_code: code,
          action: 'accept',
          recipient_notes: notes || null,
        },
      });

      if (error) throw error;

      toast.success('Transfer accepted! Animal added to your collection.');
      navigate('/admin/animals');
    } catch (error: any) {
      console.error('Error accepting transfer:', error);
      toast.error(error.message || 'Failed to accept transfer');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    setProcessing(true);
    try {
      const { error } = await supabase.functions.invoke('accept-transfer', {
        body: {
          verification_code: code,
          action: 'decline',
          recipient_notes: notes || null,
        },
      });

      if (error) throw error;

      toast.success('Transfer declined');
      navigate('/');
    } catch (error: any) {
      console.error('Error declining transfer:', error);
      toast.error(error.message || 'Failed to decline transfer');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadDocument = () => {
    window.open(
      `https://crpxcsrljmdcrgnedfxb.supabase.co/functions/v1/get-transfer-document?code=${code}`,
      '_blank'
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!transfer) {
    return null;
  }

  const scope = transfer.scope_json || {};
  const includedData = [];
  if (scope.include_feeding) includedData.push('Feeding logs');
  if (scope.include_growth) includedData.push('Growth data');
  if (scope.include_environment) includedData.push('Environment records');
  if (scope.include_reproduction) includedData.push('Breeding records');

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted py-12 px-4">
      <div className="container max-w-3xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Animal Transfer Request</h1>
          <p className="text-muted-foreground">
            You've received an animal transfer on Zoocertica
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">
                  {transfer.animal.common_name || transfer.animal.species}
                </CardTitle>
                <CardDescription className="mt-1">
                  ID: {transfer.animal.identifier || 'N/A'}
                </CardDescription>
              </div>
              <Badge variant={transfer.status === 'pending' ? 'secondary' : 'default'}>
                {transfer.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Species</p>
                <p className="font-medium">{transfer.animal.species}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Initiated</p>
                <p className="font-medium">
                  {new Date(transfer.initiated_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {includedData.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Included Historical Data:</p>
                <div className="flex flex-wrap gap-2">
                  {includedData.map((item, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {transfer.initiator_notes && (
              <div>
                <Label>Sender's Note:</Label>
                <p className="text-sm bg-muted p-3 rounded-md mt-1">
                  {transfer.initiator_notes}
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Your Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this transfer..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                onClick={handleAccept}
                disabled={processing || transfer.status !== 'pending'}
                className="flex-1"
                size="lg"
              >
                {processing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}
                {user ? 'Accept Transfer' : 'Sign Up & Accept'}
              </Button>
              
              <Button
                onClick={handleDecline}
                disabled={processing || transfer.status !== 'pending'}
                variant="outline"
                size="lg"
                className="flex-1"
              >
                {processing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                Decline Transfer
              </Button>

              <Button
                onClick={handleDownloadDocument}
                variant="secondary"
                size="lg"
              >
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </div>

            {!user && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mt-4">
                <p className="text-sm text-center">
                  <strong>New to Zoocertica?</strong> Create a free account to accept this transfer
                  and start managing your animal collection professionally.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader>
            <CardTitle className="text-lg">About Zoocertica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>
              <strong>Comprehensive Record Keeping:</strong> Track feeding, health, breeding, growth, and environmental data
            </p>
            <p>
              <strong>Secure Transfers:</strong> Transfer animals with complete historical records and certificates
            </p>
            <p>
              <strong>Interactive Analytics & Real-Time Metrics:</strong> Visualize trends and breeding success rates
            </p>
            <p>
              <strong>Legal Compliance:</strong> Stay up-to-date with state-specific wildlife regulations
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptTransfer;
